import PlaceCardContent from '@/components/templates/placeCard/PlaceCardContent';
import {
  getDateFormattedForFirstAvailableTime,
  getImageSize,
  getPlacePreviewImage,
  isMobile,
  setCookie,
  shouldDisplayReviews,
} from '@/helpers';
import { useIsInView } from '@/hooks/useIsInView';
import { LoginContext } from '@/hooks/useLogin';
import useMobileView from '@/hooks/useMobileView';
import { _s, _t } from '@/locale';
import { placeService } from '@/services';
import { SearchPlaceSubscriptionType } from '@/types/api/services/search';
import { Fragment, SyntheticEvent, useContext, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Service } from '../../salon';

const styles = {
  card: `
  relative
  flex
  min-w-0
  flex-col
  break-words
  border
  bg-white
  rounded-lg
  shadow
  border-[1px]
  bg-white
  w-full
  p-4
  sm:hover:shadow-cardHover
  duration-200
  group
  max-w-[400x]
  sm:max-w-full
  w-full
  sm:w-auto
  mx-auto
  sm:mx-0
  `,
};

const PlaceCard = (props) => {
  const { isMobileView } = useMobileView();
  const dispatch = useDispatch();
  const cardRef = useRef<HTMLElement>(null);
  const subscriptionType: SearchPlaceSubscriptionType = props.place?.about?.settings?.subscriptionType || 'stable';
  const cardIsInView = useIsInView(cardRef);
  const loginContext = useContext(LoginContext);

  if (props.source.includes('serp-page') && cardIsInView) {
    placeService.storeSerpImpressions([props.place.id]);
  }

  const setHover = (placeId) => {
    const { fireHover } = props;
    if (fireHover) {
      fireHover(placeId);
    }
  };

  const setHoverOut = (placeId) => {
    const { fireHoverOut } = props;
    if (fireHoverOut) {
      fireHoverOut(placeId);
    }
  };

  const handleClick = (e) => {
    const { place } = props;

    if (place) {
      if (place.topSearch && place.topSearch.bidId) {
        placeService.topOfSearchClicked(place.topSearch.bidId);
        setCookie('clickedTopPlaces_' + place.id, place.topSearch.bidId, {
          path: '/',
          expires: new Date(Date.now() + 30 * 86400 * 1000),
        });
      } else if (
        props.source === 'serp-page-top' &&
        place.about &&
        place.about.settings &&
        place.about.settings.topOfSearch
      ) {
        sessionStorage.setItem('ClickedOffer', place.id);
      }
    }

    if (props.onClickPlace) {
      props.onClickPlace(e);
    }
  };

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    props.removeAction(e);
  };

  const getMainImage = () => {
    const { place } = props || {};
    const { images } = place.about || {};
    const imagesLength = (images || []).length;
    if (!images || imagesLength === 0) return null;

    const mainImage = getImageSize(images[0], isMobileView ? 'big' : 'medium_l', place.about.slug + '-1');
    if (!mainImage) return null;

    const handleImageError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
      const fallbackDimensions = isMobile() ? '300x200' : '390x260';
      e.currentTarget.src = `/images/bd-fallback-img${fallbackDimensions}.jpg`;
    };

    return (
      <img loading="lazy" className="h-full w-full object-cover" src={mainImage} alt="" onError={handleImageError} />
    );
  };

  const getFirstAvailableTime = (place) => {
    const { firstAvailableTime, timezone } = place;
    return firstAvailableTime ? getDateFormattedForFirstAvailableTime(firstAvailableTime, timezone) : null;
  };

  const { place, source, className, onBookClick } = props || {};
  if (!place || !place.about) {
    return null;
  }
  const { rating, about } = place;
  const { images, welcomeText, searchResultText } = about || {};
  const address = place.contact && place.contact.address ? place.contact.address : null;
  const showAddress = address && (address.city || address.street);
  const showRatings = shouldDisplayReviews(place) && rating && rating.count > 4;
  const firstAvailableTime = !props.hasLoginForLastVisited && getFirstAvailableTime(place);
  const hasNewServices = source === 'favorites' && place.newServices && place.newServices.length ? true : false;
  const hasMainImage = (images || []).length > 0;
  const isProfilePremium = place.about.settings?.subscriptionType === 'profilepagepremium';

  const cardHeight = 'h-full';

  // Resized image (thumbnail)
  const cardImage = getPlacePreviewImage(place);

  return (
    <Fragment>
      <Link
        key={place.id}
        ref={(r) => {
          cardRef.current = r;
          if (props.myRef) props.myRef(r);
        }}
        to={{
          pathname: '/places/' + place.about.slug + '-' + place.id,
          search: props.isSistaminutenSearch ? 'sistaminuten=1' : undefined,
          state: { fromList: true, searchParams: props.searchParams },
        }}
        onClick={handleClick}
        className={`${styles.card} ${cardHeight} ${
          className ? className : ''
        } border-[rgba(0,   0, 0, 0.125)] border-none
        `}
        onMouseOver={() => setHover(place.id)}
        onMouseOut={() => setHoverOut(place.id)}
        style={!hasNewServices ? { height: 'auto' } : {}}>
        <PlaceCardContent
          place={place}
          address={address}
          cardImage={cardImage}
          getMainImage={getMainImage}
          hasMainImage={hasMainImage}
          showRatings={showRatings}
          showAddress={showAddress}
          rating={rating}
          firstAvailableTime={firstAvailableTime}
          handleRemove={handleRemove}
          removeAction={props.removeAction}
          source={props.source}
          welcomeText={searchResultText ?? welcomeText}
          subscriptionType={subscriptionType}
        />

        {props.hasLoginForLastVisited && (
          <div className="absolute inset-0 flex items-end justify-center ">
            <div className="z-[2] mb-4">
              {loginContext.renderLoginButton({
                label: _t('loginAndViewLastVisited'),
                initiatedFrom: 'recently_viewed',
              })}
            </div>
            <div className="absolute inset-0 z-[1] bg-white opacity-75"></div>
          </div>
        )}
        {hasNewServices && subscriptionType !== 'essential' && subscriptionType !== 'mini' && !isProfilePremium && (
          <div className="border-black-200 text-black-900 mt-4 w-full border-t-[1px] pt-4">
            {place.newServices.map((service, i) => {
              return (
                <div className="mb-2" key={i}>
                  <ul>
                    <li style={{ paddingBottom: 4 }}>
                      <span
                        style={{ background: 'rgba(234, 87, 134, 0.1)' }}
                        className="text-m rounded px-2 py-[6px] font-semibold text-[#ea5786]">
                        {_s('new')}
                      </span>
                    </li>
                    <Service
                      section="none"
                      place={place}
                      service={service}
                      key={service.id}
                      source={source}
                      fakeButton={true}
                      onBookClick={onBookClick}
                    />
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </Link>
    </Fragment>
  );
};

export default withRouter(PlaceCard);
